import { ReactNode } from "react";

//Enums
export enum ActionType {
  SET_MODAL = "SET_MODAL",
  SET_TOAST = "SET_TOAST",
  SET_REQUEST_LOADING = "SET_REQUEST_LOADING",
  SET_SPINNER = "SET_SPINNER",
  SET_EXTENSION_INSTALLED = "SET_EXTENSION_INSTALLED",
  SET_EXTENSION_ID = "SET_EXTENSION_ID",
  SET_EXTENSION_VERSION = "SET_EXTENSION_VERSION",
  SET_ONBOARDING_STEP = "SET_ONBOARDING_STEP",
  SET_HEADER_STATE = "SET_HEADER_STATE",
  SET_SETTINGS_STATE = "SET_SETTINGS_STATE",
  RESET_STORE = "RESET_STORE",
  SET_DARK_MODE = "SET_DARK_MODE",
  SET_SIDEBAR = "SET_SIDEBAR",
  SET_LEADDELTA_TAB_IS_ACTIVE = "SET_LEADDELTA_TAB_IS_ACTIVE",
  SET_SYNC_COMPLETION_PERCENTAGE = "SET_SYNC_COMPLETION_PERCENTAGE",
  OPEN_CHAT = 'OPEN_CHAT',
  CLOSE_CHAT = 'CLOSE_CHAT',
  UPDATE_CHAT = 'UPDATE_CHAT',
  UPDATE_CONVERSATION = 'UPDATE_CONVERSATION',
  SET_NAV_COLLAPSED = "SET_NAV_COLLAPSED",
  SET_MOBILE_NAV_OPEN = "SET_MOBILE_NAV_OPEN",
}

export enum AppRoutes {
  HOME = "/",
  PROCESSING = "/processing",
  DISABLED = "/disabled",
  PRICING = "/pricing",

  INVITE = "/invite",
  SIGNUP = "/signup",
  SIGNIN = "/signin",
  SSO_CALLBACK = "/sso-callback",
  FORGOT_PASSWORD = "/forgotPassword",
  DELETE_USER_CONFIRMATION = "/delete-confirmation",

  ONBOARDING = "/onboarding",
  ONBOARDING_SURVEY = "/onboarding/survey",
  ONBOARDING_ALL_SET = "/onboarding/all-set",
  ONBOARDING_MOBILE_FINISH = "/onboarding/mobile-finish",
  ONBOARDING_EXTENSION_INSTALL = "/onboarding/extensionInstall",
  ONBOARDING_UNSUPPORTED_BROWSER = "/onboarding/unsupported-browser",
  ONBOARDING_LINKEDIN = "/onboarding/linkedIn",
  ONBOARDING_SYNC_NETWORK = "/onboarding/sync-network",

  DASHBOARD = "/dashboard",
  CONNECTIONS = "/connections",
  TAGS = "/tags",
  TASKS = "/tasks",
  TEMPLATES = "/templates",
  NOTES = "/notes",
  POSTS = "/posts",
  INTEGRATIONS = "/integrations",
  CSV_ENRICHMENT = "/csvEnrichment",
  PROSPECTS = "/prospects",
  CUSTOM_CONTACT_PROPERTIES = "/settings/custom-contact-properties",
  AI_CHAT = "/aiChat",
  INBOX = "/inbox",

  SETTINGS = "/settings",
  SETTINGS_DATA_MANAGEMENT = "/settings/dataManagement",
  SETTINGS_WORKSPACE_ADMIN = "/settings/admin",
  SETTINGS_BILLING = "/settings/billing",
  SETTINGS_EXTENSION = "/settings/extension",
  /** Campaign settings: one page, a tab per route below it. */
  SETTINGS_CAMPAIGNS = "/settings/campaigns",
  SETTINGS_ACCOUNT_ACTIVITY = "/settings/campaigns/account-activity",
  SETTINGS_SUPPRESSION_LIST = "/settings/campaigns/suppression-lists",
  SETTINGS_SENDING_ACCOUNTS = "/settings/campaigns/sending-accounts",
  SETTINGS_CONVERSATION_TAGS = "/settings/conversation-tags",
  /** @deprecated Use SETTINGS_CONVERSATION_TAGS */
  SETTINGS_LABELS = "/settings/conversation-tags",
  SETTINGS_LINKEDIN = "/settings/linkedin",
  CSV_ENRICHMENT_SETTINGS = "/settings/csvEnrichment",
  SETTINGS_FEED_MANAGEMENT = "/settings/feeds",
  SETTINGS_HUBSPOT = "/settings/hubspot",

  BILLING = "/billing",
  BILLING_PLAN = "/billing/plan",
  BILLING_PAYMENT = "/billing/payment",
  BILLING_PROCESSING = "/billing/processing",

  WORKSPACE_MANAGEMENT = "/workspace-management",
  CREATE_WORKSPACE = "/createWorkspace",

  INFO = "/info",
  REDIRECT = "/redirect",

  CAMPAIGNS = "/campaigns",
  /** Warm lead radar: LinkedIn signals read through cloud-connected accounts. */
  SIGNALS = "/signals",
  CAMPAIGN_NEW = "/campaigns/new",
  CAMPAIGN_EDIT = "/campaigns/:id/edit",
  CAMPAIGN_LEADS = "/campaigns/:id/leads",

  CSV_IMPORT = "/csv/import",
  CSV_MAPPING = "/csv/mapping",
  HUBSPOT_MAPPING = "/hubspot/mapping",

  LINKEDIN_TEST = "/dev/linkedin-test",
}

export enum SidebarTypes {
  BULK_MESSAGE = "bulkMessage",

  //filtering
  CONNECTION_FILTER = "connectionFilter",
  TAG_FILTER = "tagFilter",
  CONNECTION_ACTIONS = "connectionActions",
  APPLY_TAG = "applyTags",
  REMOVE_TAG = "removeTags",
  NOTE_FILTER = "noteFilter",
  TASKS_FILTER = "tasksFilter",
  TEMPLATES_FILTER = "templatesFilter",

  //create
  WORKSPACE_CREATE = "workspaceCreate",

  TAG_EDIT_CREATE = "tagCreateEdit",
  TASK_EDIT_CREATE = "taskCreateEdit",
  NOTE_EDIT_CREATE = "noteCreateEdit",
  TEMPLATES_EDIT_CREATE = "templatesCreateEdit",
  CUSTOM_FIELD_EDIT_CREATE = "customFieldCreateEdit",

  //integrations
  PABBLY = "pabbly",
  HUBSPOT = "hubspot",

  SAVE_FILTER = "saveFilter",

  CONNECTION_NETWORK = "connectionNetwork",
}

export enum AppLiterals {
  WORKSPACE = "workspace",
  INDIVIDUAL = "individual",
}

//Interfaces
export interface IToast {
  type?: "success" | "danger" | "info" | "warning" | "error" | "reSendMessage" | "removalScheduled";
  duration?: number;
  message?: string;
  onClick?: {
    onClickHandler?: (e: any) => void;
    onCloseHandler?: (e: any) => void;
    label?: string;
  };
}

export interface IModal {
  type?: string;
  headingText?: ReactNode;
  descriptionText?: ReactNode;
  descriptionTextBold?: ReactNode;
  props?: any;
  onConfirmHandler?: (e: any) => void;
  onCancelHandler?: (e: any) => void;
}

export interface IRequestLoading {
  [key: string]: boolean;
}

export interface ISidebar {
  type: string;
  zIndex: number;
  props?: any;
}

//States
export type TAppState = {
  modal?: IModal;
  toast?: IToast;
  requestLoading?: IRequestLoading;
  counterValue?: number;
  spinner?: boolean;
  extensionInstalled?: boolean;
  extensionId?: string;
  extensionVersion?: string;
  onboardingStep: number;
  headerState?: THeaderState;
  settingsState?: THeaderState;
  sidebar: ISidebar[];
  darkMode?: boolean;
  connectionsFilter?: boolean; //same story, remove these please
  leadDeltaTabIsActive?: boolean;
  syncCompletionPercentage?: number;
  chatPopups?: any;
  navCollapsed?: boolean;
  mobileNavOpen?: boolean;
};

export type TMenuItemState = {
  isNew?: boolean;
  /** Nav pill label (e.g. BETA). When set, shown instead of default NEW from isNew. */
  badge?: string;
  title: string;
  visible: boolean;
  enabled: boolean;
  route?: string;
  routeInternal?: string;
  extension?: string;
  icon?: string;
  destructive?: boolean;
  showInstallExt?: boolean;
  tooltip?: string;
  onClickHandler?: (e: any) => void;
  /** Single nav Inbox entry: click opens extension or /inbox based on plan. */
  inboxNav?: boolean;
};

export type THeaderState = {
  [key: string]: TMenuItemState;
};

export const headerRoutes: THeaderState = {
  home: {
    isNew: false,
    title: "Home",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.HOME,
    icon: "home",
  },
  dashboard: {
    isNew: false,
    title: "Dashboard",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.DASHBOARD,
    icon: "folderText",
  },
  connections: {
    isNew: false,
    title: "Contacts",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.CONNECTIONS,
    icon: "fileText",
  },
  inbox: {
    isNew: false,
    title: "Inbox",
    visible: true,
    enabled: true,
    inboxNav: true,
    icon: "envelope",
  },
  tags: {
    isNew: false,
    title: "Tags",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.TAGS,
    icon: "label",
  },
  tasks: {
    isNew: false,
    title: "Tasks",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.TASKS,
    icon: "pen",
  },
  templates: {
    isNew: false,
    title: "Templates",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.TEMPLATES,
    icon: "paperText",
  },
  notes: {
    isNew: false,
    title: "Notes",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.NOTES,
    icon: "note",
  },
  posts: {
    isNew: false,
    title: "Custom feeds",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.POSTS,
    icon: "paperText",
  },
  integrations: {
    isNew: false,
    title: "Integrations/MCP",
    visible: false,
    enabled: true,
    routeInternal: AppRoutes.INTEGRATIONS,
    icon: "puzzlePiece",
  },
  csvEnrichment: {
    isNew: false,
    title: "CSV Data Finder",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.CSV_ENRICHMENT,
    icon: "aiAssistant",
  },
  prospects: {
    isNew: false,
    title: "LeadSearch DB",
    visible: true,
    enabled: true, // runtime disabled in Header based on workspace plan
    routeInternal: AppRoutes.PROSPECTS,
    icon: "puzzlePiece",
  },
  aiChat: {
    isNew: false,
    title: "AI Chat",
    // AI Chat is sunset for now (see SideNav); kept hidden so it can be brought back.
    visible: false,
    enabled: true,
    routeInternal: AppRoutes.AI_CHAT,
    icon: "aiAssistant",
  },
};

export const settingsRoutes: THeaderState = {
  workspaceAdmin: {
    title: "Admin",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_WORKSPACE_ADMIN,
    icon: "profiles",
  },
  backgroundUpdate: {
    title: "Background update",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_EXTENSION,
    icon: "refresh",
  },
  campaignSettings: {
    title: "Campaign settings",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_CAMPAIGNS,
    icon: "settingsSend",
  },
  conversationTags: {
    title: "Conversation Tags",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_CONVERSATION_TAGS,
    icon: "label",
  },
  dataManagement: {
    title: "Data management",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_DATA_MANAGEMENT,
    icon: "data",
  },
  customContactProperties: {
    title: "Custom contact properties",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.CUSTOM_CONTACT_PROPERTIES,
    icon: "customField",
  },
  billing: {
    title: "Billing",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_BILLING,
    icon: "card",
  },
  linkedin: {
    title: "LinkedIn sidebar",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_LINKEDIN,
    icon: "sidebar",
  },
  csvEnrichmentSettings: {
    title: "CSV Data Finder",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.CSV_ENRICHMENT_SETTINGS,
    icon: "enrich",
  },
  feedManagement: {
    title: "Feeds",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_FEED_MANAGEMENT,
    icon: "feedBlue",
  },
  hubspotFieldMapping: {
    title: "HubSpot",
    visible: true,
    enabled: true,
    routeInternal: AppRoutes.SETTINGS_HUBSPOT,
    icon: "hubspot",
  },
};

//Actions
interface IModalAction {
  type: ActionType.SET_MODAL;
  payload: TAppState["modal"];
}

interface IShowToastAction {
  type: ActionType.SET_TOAST;
  payload: TAppState["toast"];
}

interface IRequestLoadingAction {
  type: ActionType.SET_REQUEST_LOADING;
  payload: TAppState["requestLoading"];
}

interface ISetSpinner {
  type: ActionType.SET_SPINNER;
  payload: TAppState["spinner"];
}

interface ISetExtensionInstalled {
  type: ActionType.SET_EXTENSION_INSTALLED;
  payload: TAppState["extensionInstalled"];
}

interface ISetExtensionId {
  type: ActionType.SET_EXTENSION_ID;
  payload: TAppState["extensionId"];
}

interface ISetExtensionVersion {
  type: ActionType.SET_EXTENSION_VERSION;
  payload: TAppState["extensionVersion"];
}

interface ISetOnboardingStep {
  type: ActionType.SET_ONBOARDING_STEP;
  payload: TAppState["onboardingStep"];
}

interface ISetHeaderState {
  type: ActionType.SET_HEADER_STATE;
  payload: TAppState["headerState"];
}

interface ISetSettingsState {
  type: ActionType.SET_SETTINGS_STATE;
  payload: TAppState["settingsState"];
}

interface ISetDarkMode {
  type: ActionType.SET_DARK_MODE;
  payload: TAppState["darkMode"];
}

interface IResetStoreAction {
  type: ActionType.RESET_STORE;
}

interface ISetSidebar {
  type: ActionType.SET_SIDEBAR;
  payload: TAppState["sidebar"];
}

interface ISetLeadDeltaTabIsActive {
  type: ActionType.SET_LEADDELTA_TAB_IS_ACTIVE;
  payload: TAppState["leadDeltaTabIsActive"];
}

interface ISetSyncCompletionPercentage {
  type: ActionType.SET_SYNC_COMPLETION_PERCENTAGE;
  payload: TAppState["syncCompletionPercentage"];
}

interface ISetNavCollapsed {
  type: ActionType.SET_NAV_COLLAPSED;
  payload: TAppState["navCollapsed"];
}

interface ISetMobileNavOpen {
  type: ActionType.SET_MOBILE_NAV_OPEN;
  payload: TAppState["mobileNavOpen"];
}

export interface IOpenChatAction {
  type: ActionType.OPEN_CHAT;
  payload: any;
}

export interface IUpdateChatAction {
  type: ActionType.UPDATE_CHAT;
  payload: any;
}

export interface IUpdateConversationAction {
  type: ActionType.UPDATE_CONVERSATION;
  payload: any;
}

export interface ICloseChatAction {
  type: ActionType.CLOSE_CHAT;
  payload: string;
}

export type TAppAction =
  | IModalAction
  | IShowToastAction
  | IRequestLoadingAction
  | ISetSpinner
  | ISetExtensionInstalled
  | IResetStoreAction
  | ISetExtensionId
  | ISetExtensionVersion
  | ISetOnboardingStep
  | ISetHeaderState
  | ISetSettingsState
  | ISetSidebar
  | ISetDarkMode
  | ISetLeadDeltaTabIsActive
  | ISetSyncCompletionPercentage
  | ISetNavCollapsed
  | ISetMobileNavOpen
  | IOpenChatAction
  | IUpdateChatAction
  | IUpdateConversationAction
  | ICloseChatAction
//export type TDispatchType = (args: TAppState) => TAppAction
